import {
  writeBatch,
  doc,
  deleteField,
  collection,
  query,
  limit,
  getDocs,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { db, storage } from "../../../../connection";
import logger from "../../../../../../utils/logger";
import {
  buildContentImagePath,
  deleteImage,
} from "../../../../commonServices/images";

/**
 * Resolves the content type for an image file to be uploaded.
 * Ensures we never upload with application/octet-stream or missing type.
 *
 * Priority:
 * 1. File's own MIME type (if it's a valid image type)
 * 2. Fallback to image/jpeg
 */
const resolveImageContentType = (imageFile) => {
  const type = imageFile?.type;
  if (type && type.startsWith("image/")) {
    return type;
  }

  // Log unexpected types so we can trace the "document" type bug
  if (type) {
    logger.warn(
      "[uploadImage] File has non-image MIME type, defaulting to image/jpeg",
      {
        originalType: type,
        fileName: imageFile?.name,
        size: imageFile?.size,
      },
    );
  } else {
    logger.warn(
      "[uploadImage] File has no MIME type, defaulting to image/jpeg",
      {
        fileName: imageFile?.name,
        size: imageFile?.size,
        isFile: imageFile instanceof File,
        isBlob: imageFile instanceof Blob,
      },
    );
  }

  return "image/jpeg";
};

/**
 * Uploads an image file to Firebase Storage with proper metadata.
 * @param {string} path - Storage path
 * @param {File|Blob} imageFile - The image to upload
 * @returns {Promise<string>} Download URL
 */
const uploadImage = async (path, imageFile) => {
  const storageRef = ref(storage, path);
  const contentType = resolveImageContentType(imageFile);

  logger.info("[uploadImage] Uploading", {
    path,
    contentType,
    size: imageFile?.size,
    originalType: imageFile?.type,
  });

  await uploadBytes(storageRef, imageFile, {
    contentType,
  });

  return getDownloadURL(storageRef);
};

/**
 * Updates content in Firebase
 * @param {Object} contentData - Validated content data from toApiFormat
 * @param {string} contentId - ID of content to update
 * @param {Object} options - Update options
 * @param {File} [options.imageFile] - New image file to upload
 * @param {boolean} [options.deleteImage] - Whether to delete existing image
 * @param {boolean} [options.isParent] - Whether this is a parent group event (uses parentGroups/{groupId}/events path)
 * @param {string} [options.customGroupId] - Custom groupId to use instead of data.groupId (for parent groups)
 * @returns {Promise<Object>} Updated content with ID
 */
export async function updateEventService(contentData, contentId, options = {}) {
  const {
    imageFile,
    deleteImage: shouldDelete = false,
    isParent = false,
    customGroupId,
  } = options;

  const data = JSON.parse(JSON.stringify(contentData));

  const batch = writeBatch(db);
  const groupId = customGroupId || data.groupId;

  if (!groupId) {
    throw new Error("groupId is required for content update");
  }

  // Use appropriate path based on isParent flag
  const adminPostDoc = isParent
    ? doc(db, "parentGroups", groupId, "events", contentId)
    : doc(db, "groupAdminPost", groupId, "posts", contentId);

  try {
    if (shouldDelete) {
      try {
        const imagePath = `${groupId}/content/images/${contentId}`;
        const storageRef = ref(storage, imagePath);
        await deleteObject(storageRef);
        data.details.image = "";
      } catch (err) {
        logger.error("Failed to delete image:", err);
      }
    }

    // Handle image upload
    if (imageFile) {
      const imagePath = `${groupId}/content/images/${contentId}`;
      const downloadUrl = await uploadImage(imagePath, imageFile);
      data.details.image = downloadUrl;
    }

    // Prepare data for Firebase
    const firebaseData = { ...data };
    delete firebaseData.groupId;

    // Update rsvp.max without overwriting existing attending/maybe/no counts
    if (firebaseData.rsvpMax != null) {
      firebaseData["rsvp.max"] = firebaseData.rsvpMax;
    } else {
      firebaseData["rsvp.max"] = deleteField();
    }
    delete firebaseData.rsvpMax;

    // Handle additional locations migration
    if (
      Array.isArray(firebaseData.additionalLocations) &&
      firebaseData.additionalLocations.length > 0
    ) {
      firebaseData.mapsData = deleteField();
    }

    // Execute batch update
    batch.update(adminPostDoc, firebaseData);
    await batch.commit();

    return { ...firebaseData, id: contentId };
  } catch (error) {
    logger.error("Failed to update content:", error);
    throw new Error(
      error.message || "Failed to update content. Please try again.",
    );
  }
}

/**
 * Creates new content in Firebase
 * @param {Object} contentData - Validated content data from toApiFormat
 * @param {Object} options - Create options
 * @param {File} [options.imageFile] - Image file to upload
 * @param {boolean} [options.isParentPost] - Whether this is a parent post (uses parentGroups/{groupId}/events path)
 * @param {string} [options.customGroupId] - Custom groupId to use instead of data.groupId (for parent groups)
 * @returns {Promise<Object>} Created content with ID
 */
export async function createEventService(contentData, options = {}) {
  const { imageFile, isParentPost = false, customGroupId } = options;

  const data = JSON.parse(JSON.stringify(contentData));

  const batch = writeBatch(db);
  const groupId = customGroupId || data.groupId;

  if (!groupId) {
    throw new Error("groupId is required for content creation");
  }

  // Use appropriate path based on isParentPost flag
  const adminPostDoc = isParentPost
    ? doc(collection(db, "parentGroups", groupId, "events"))
    : doc(collection(db, "groupAdminPost", groupId, "posts"));

  try {
    // Add metadata
    data.rsvp = {
      attending: 0,
      maybe: 0,
      no: 0,
      ...(data.rsvpMax != null ? { max: data.rsvpMax } : {}),
    };
    delete data.rsvpMax;
    data.createdOn = parseInt(Date.now() / 1000);

    if (isParentPost) {
      data.isParentPost = true;
    }

    if (imageFile) {
      const imagePath = `${groupId}/content/images/${adminPostDoc.id}`;
      const downloadUrl = await uploadImage(imagePath, imageFile);
      data.details.image = downloadUrl;
    }

    const firebaseData = { ...data };
    delete firebaseData.groupId;

    batch.set(adminPostDoc, firebaseData);
    await batch.commit();

    return { ...firebaseData, id: adminPostDoc.id };
  } catch (error) {
    logger.error("Failed to create content:", error);
    throw new Error(
      error.message || "Failed to create content. Please try again.",
    );
  }
}

export async function deleteEventService(contentId, groupId, options = {}) {
  const { deleteImage: shouldDeleteImage = false, isParent = false } = options;

  if (!contentId || !groupId) {
    throw new Error("contentId and groupId are required for deletion");
  }

  try {
    const rsvpCollectionPath = isParent
      ? collection(db, "parentGroups", groupId, "events", contentId, "rsvp")
      : collection(db, "groupAdminPost", groupId, "posts", contentId, "rsvp");

    const rsvpQuery = query(rsvpCollectionPath, limit(1));
    const querySnapshot = await getDocs(rsvpQuery);

    if (querySnapshot.docs && querySnapshot.docs.length > 0) {
      throw new Error("This content has RSVPs and cannot be deleted");
    }

    if (shouldDeleteImage) {
      try {
        const imagePath = buildContentImagePath(groupId, contentId);
        await deleteImage(imagePath);
      } catch (err) {
        logger.error("Failed to delete content image:", err);
      }
    }

    const docRef = isParent
      ? doc(db, "parentGroups", groupId, "events", contentId)
      : doc(db, "groupAdminPost", groupId, "posts", contentId);

    const batch = writeBatch(db);
    batch.delete(docRef);
    await batch.commit();
  } catch (error) {
    logger.error("Failed to delete content:", error);
    throw new Error(
      error.message || "Failed to delete content. Please try again.",
    );
  }
}
